import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { REGISTER_URL } from "@/lib/api";

export const LOGO = "/brand/logo.png";

const links = [
  ["Why Ballypatrick", "#manifesto", "manifesto"],
  ["Assurance", "#assurance", "assurance"],
  ["Collection", "#collection", "collection"],
  ["Transport", "#transport", "transport"],
  ["Graduates", "#graduates", "graduates"],
  ["Archive", "#archive", "archive"],
];

export const Nav = () => {
  const { scrollY } = useScroll();
  const bg = useTransform(scrollY, [0, 120], ["rgba(11,22,48,0)", "rgba(11,22,48,0.88)"]);
  const border = useTransform(scrollY, [0, 120], ["rgba(212,175,55,0)", "rgba(212,175,55,0.15)"]);
  const [open, setOpen] = useState(false);
  useEffect(() => { document.documentElement.classList.toggle("lenis-stopped", open); }, [open]);

  return (
    <>
      <motion.header style={{ backgroundColor: bg, borderColor: border }} className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-xl" data-testid="site-nav">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 h-16 lg:h-20 flex items-center justify-between gap-4">
          <a href="#top" className="flex items-center gap-3 shrink-0" data-testid="ballypatrick-nav-logo" aria-label="Ballypatrick Auctions home">
            <img src={LOGO} alt="Ballypatrick Auctions" className="h-10 lg:h-12 w-auto" />
          </a>
          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {links.map(([label, href, id]) => (
              <a key={href} href={href} data-testid={`ballypatrick-nav-${id}`} className="link-line text-[11px] tracking-[0.18em] uppercase text-[#AEB7C9] hover:text-[#F8F6F0] transition-colors duration-300">{label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={REGISTER_URL} target="_blank" rel="noreferrer" data-testid="ballypatrick-btn-register-bid-header" className="btn-gold !py-2.5 !px-4 !hidden md:!inline-flex">Register to bid <ArrowUpRight size={14} /></a>
            <button className="lg:hidden text-[#F8F6F0] p-2 -mr-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu" aria-expanded={open}>
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="lg:hidden fixed inset-0 z-40 bg-[#0B1630] navy-glow pt-24 px-6 pb-28 flex flex-col" data-testid="nav-mobile-menu">
            <nav className="flex flex-col divide-y divide-white/8" aria-label="Mobile">
              {links.map(([label, href, id], i) => (
                <motion.a key={href} href={href} onClick={() => setOpen(false)} data-testid={`ballypatrick-nav-mobile-${id}`}
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, duration: 0.5 }}
                  className="flex items-center justify-between py-5 font-display text-3xl text-[#F8F6F0]">
                  {label} <ArrowUpRight size={18} className="text-[#D4AF37]" />
                </motion.a>
              ))}
            </nav>
            <p className="mt-auto eyebrow">Auction V · 28 December 2026</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#D4AF37]/20 bg-[#0B1630]/[.92] backdrop-blur-xl px-4 py-3 flex items-center gap-3" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="mobile-cta-bar">
        <a href="#collection" className="btn-ghost flex-1 !py-3" data-testid="mobile-cta-collection">Collection</a>
        <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="btn-gold flex-1 !py-3" data-testid="ballypatrick-btn-register-bid-mobile">Register to bid <ArrowUpRight size={14} /></a>
      </div>
    </>
  );
};
